/** US IANA timezones allowed for organizations and role profiles (caretaker, provider, practitioner). */
export const US_ORG_TIMEZONES = [
  { label: "Eastern — New York (ET)", value: "America/New_York" },
  { label: "Central — Chicago (CT)", value: "America/Chicago" },
  { label: "Mountain — Denver (MT)", value: "America/Denver" },
  { label: "Pacific — Los Angeles (PT)", value: "America/Los_Angeles" },
] as const;

export type UsOrgTimezoneValue = (typeof US_ORG_TIMEZONES)[number]["value"];

export const US_ORG_TIMEZONE_VALUES: UsOrgTimezoneValue[] = US_ORG_TIMEZONES.map(
  (tz) => tz.value,
);

/** Options for `RhfSelect` and similar selects. */
export const US_ORG_TIMEZONE_SELECT_OPTIONS = US_ORG_TIMEZONES.map((tz) => ({
  label: tz.label,
  value: tz.value,
}));

export const DEFAULT_US_ORG_TIMEZONE: UsOrgTimezoneValue = "America/New_York";

/** Same options as org profiles — used for patient notification timezone selects. */
export const PATIENT_TIMEZONES = [
  { label: "Eastern Time (ET)", value: "America/New_York" },
    { label: "Central Time (CT)", value: "America/Chicago" },
    { label: "Mountain Time (MT)", value: "America/Denver" },
    { label: "Pacific Time (PT)", value: "America/Los_Angeles" },
] as const;;

const PATIENT_TIMEZONE_LABEL_BY_VALUE = Object.fromEntries(
  PATIENT_TIMEZONES.map((tz) => [tz.value, tz.label]),
) as Record<UsOrgTimezoneValue, string>;

/**
 * Maps an IANA US org timezone id to the UI label (e.g. `America/Chicago` → `Central — Chicago (CT)`).
 * Unknown values are returned unchanged; empty values become `—`.
 */
export function formatUsTimezoneLabel(
  iana: string | null | undefined,
  fallback = "—",
): string {
  if (iana == null || typeof iana !== "string") return fallback;
  const tz = iana.trim();
  if (!tz) return fallback;
  const label =
    PATIENT_TIMEZONE_LABEL_BY_VALUE[tz as UsOrgTimezoneValue] ??
    PATIENT_TIMEZONES.find(
      (entry) => entry.value.toLowerCase() === tz.toLowerCase(),
    )?.label;
  return label ?? tz;
}
